'use client'

import React from 'react'
import Image from 'next/image'
import { CMSLink } from '@/components/Link'
import type { CtaBannerBlock as CtaBannerBlockProps } from '@/payload-types'
import { motion } from 'framer-motion'

type MediaInfo = { url?: string; width?: number; height?: number }

function getMediaInfo(m: any): MediaInfo {
  if (!m) return {}
  const doc = m?.value ?? m
  const pick = (x: any) =>
    x && x.url ? { url: x.url, width: x.width, height: x.height } : undefined

  return (
    pick(doc?.sizes?.hero) ||
    pick(doc?.sizes?.large) ||
    pick(doc?.sizes?.card) ||
    (doc?.url ? { url: doc.url, width: doc.width, height: doc.height } : {})
  )
}

export const CtaBannerBlock: React.FC<CtaBannerBlockProps> = ({ title, links, backgrounds }) => {
  const candidates = React.useMemo<MediaInfo[]>(() => {
    if (!Array.isArray(backgrounds)) return []
    return backgrounds.map((b: any) => getMediaInfo(b)).filter(Boolean)
  }, [backgrounds])

  // pick random on client to avoid hydration mismatch
  const [bg, setBg] = React.useState<MediaInfo | null>(null)
  React.useEffect(() => {
    if (!candidates.length) return
    const idx = Math.floor(Math.random() * candidates.length)
    setBg(candidates[idx] ?? null)
  }, [candidates])

  const items: any[] = Array.isArray(links) ? (links as any[]) : []

  return (
    <section className="relative w-full">
      {/* Fixed height container */}
      <div className="relative w-full overflow-hidden h-[730px]">
        {/* Background image */}
        {bg?.url && (
          <Image
            src={bg.url}
            alt=""
            fill
            priority
            sizes="100vw"
            className="object-cover"
          />
        )}

        {/* Top gradient */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1/3 dark:h-1/2 bg-gradient-to-b from-minigreen-1 via-minigreen-1/70 dark:from-minigreen-bg-dark dark:via-minigreen-bg-dark/70 to-transparent" />

        {/* Bottom gradient */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-4/5 bg-gradient-to-t from-minigreen-7  via-minigreen-7/70 dark:from-minigreen-bg-dark dark:via-minigreen-bg-dark/70 to-transparent" />

        {/* Content overlay ~1/3 up from the bottom */}
        <div className="absolute left-0 right-0 bottom-[30%]">
          <div className="w-full px-4 text-center">
            {title ? (
              <motion.h2
                className="text-3xl md:text-5xl lg:text-6xl font-medium text-white mb-16 max-w-5xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
                viewport={{ once: true, amount: 0.4 }}
              >
                {title}
              </motion.h2>
            ) : null}

            {items.length > 0 && (
              <motion.ul
                className="flex flex-col sm:flex-row gap-5 sm:gap-6 justify-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
                viewport={{ once: true, amount: 0.3 }}
              >
                {items.map((item, i) => {
                  const link = item?.link ?? item
                  if (!link) return null
                  const key = link?.id ?? link?.url ?? link?.href ?? i
                  return (
                    <li key={key}>
                      <CMSLink {...link} />
                    </li>
                  )
                })}
              </motion.ul>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default CtaBannerBlock
